import React, { useState } from 'react'
import { observer } from 'mobx-react-lite'
const _snakeCase = require('lodash/snakeCase')

import {
  ImportContent,
  importContent,
  QuestionHelper,
  QuizHelper,
  TopicHelper,
  useStores,
} from '@trivie/core'

import { Dialog } from '@trivie/ui-web/components/dialogs/dialog'
import { FileInput } from '@trivie/ui-web/components/file-input'
import { Text } from '@trivie/ui-web/components/text'
import { color } from '@trivie/ui-web/theme'

// @ts-ignore
import { Stack } from '@mui/material'
import { ReactComponent as ExcelFile } from '@trivie/ui-web/assets/icons/Excel.svg'
import { useHistory } from 'react-router-dom'
import { ConfirmationDialog } from '@trivie/ui-web/components/dialogs/confirmation-dialog'

interface ImportContentDialogProps {
  intent: 'Topics' | 'Quizzes' | 'Questions'
  title: string
  onClose: () => void
  open: boolean
}

export const ImportContentDialog = observer((props: ImportContentDialogProps) => {
  const { intent, title, onClose, open } = props

  const { contentCreationStore, manageUserStore } = useStores()
  const { settings } = manageUserStore
  const { setImportingFilename } = contentCreationStore

  const history = useHistory()

  const [file, setFile] = useState<any>(null)
  const [error, setError] = useState<any>()
  const [showConfirm, setShowConfirm] = useState(false)
  const [dialogText, setDialogText] = useState('')
  const [dialogTitle, setDialogTitle] = useState('')
  const [confirm, setConfirm] = useState<any>()
  const [cancel, setCancel] = useState<any>()

  const onDrop = async (f: any) => {
    setFile(f)
    setError('')
    if (f) {
      try {
        const data = (await importContent(f)) as Array<ImportContent>
        setImportingFilename(f.name)

        handleImport(
          data.map((row) => {
            return {
              ...row,
              question_type: _snakeCase(row.question_type?.trim().toLocaleLowerCase()),
            }
          }),
        )
      } catch (e) {
        setError(e)
      }
    }
  }

  const downloadExcelTemplate = () => {
    const a = document.createElement('a')
    a.download = settings.new_question_types
      ? 'v8-content-import-template'
      : 'v8-content-import-template_v1'
    a.href = settings.new_question_types
      ? '/v8-content-import-template.xlsx'
      : '/v8-content-import-template_v1.xlsx'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }

  const downloadCsvTemplate = () => {
    const a = document.createElement('a')
    a.download = settings.new_question_types
      ? 'v8-content-import-template'
      : 'v8-content-import-template_v1'
    a.href = settings.new_question_types
      ? '/v8-content-import-template.csv'
      : '/v8-content-import-template_v1.csv'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }

  const TEMPLATE_URL = settings.new_question_types
    ? `https://docs.google.com/spreadsheets/d/1k4THoJ_UI17LN5NGOFd4Mpc9JCe7OAcX_--z-BnDJxc/edit?gid=0#gid=0`
    : 'https://docs.google.com/spreadsheets/d/1Ut1A2qIJoCq3g4rO0HkLvtKV5ojlpfsexNw2w69Ce04/edit?gid=0#gid=0'

  const confirmTopics = (topics) => {
    contentCreationStore.setTopics(topics as any)
    contentCreationStore.selectTopic(topics[0] as any)
    history.push('/content/creation/import')
  }

  const confirmQuizzes = (quizzes) => {
    contentCreationStore.setImportingQuizzes(quizzes)
    history.push('/content/creation/quiz/import')
  }

  const confirmQuestions = (questions) => {
    if (questions?.length) {
      questions[0].select()
      contentCreationStore.setImportingQuestions(questions)
      history.push('/content/creation/question/import')
    } else {
      setShowConfirm(false)
      setError('File must contain at least one question')
    }
  }

  const handleImport = (data) => {
    try {
      const topics = TopicHelper.importTopics(data)
      const quizzes = QuizHelper.importQuizzes(data)
      const questions = QuestionHelper.importQuestions(data)

      switch (intent) {
        case 'Topics':
          if (topics?.length) {
            confirmTopics(TopicHelper.importTopics(data, true))
          } else if (quizzes?.length) {
            setConfirm(() => () => confirmQuizzes(QuizHelper.importQuizzes(data, true)))
            setCancel(() => () => confirmTopics(TopicHelper.importTopics(data, true)))
            setDialogText(
              'Only quizzes were found in this file. Would you like to import quizzes instead?',
            )
            setDialogTitle('Import Quizzes?')
            setShowConfirm(true)
          } else if (questions?.length) {
            setConfirm(() => () => confirmQuestions(questions))
            setCancel(() => () => confirmTopics(TopicHelper.importTopics(data, true)))
            setDialogText(
              'Only questions were found in this file. Would you like to import questions instead?',
            )
            setDialogTitle('Import Questions?')
            setShowConfirm(true)
          }
          break
        case 'Quizzes':
          if (topics?.length) {
            setConfirm(() => () => confirmTopics(TopicHelper.importTopics(data, true)))
            setCancel(() => () => confirmQuizzes(QuizHelper.importQuizzes(data, true)))
            setDialogText(
              'Topics were found in this file. Would you like to import topics instead?',
            )
            setDialogTitle('Import Topics?')
            setShowConfirm(true)
          } else if (quizzes?.length) {
            confirmQuizzes(QuizHelper.importQuizzes(data, true))
          } else if (questions?.length) {
            setConfirm(() => () => confirmQuestions(questions))
            setCancel(() => () => confirmQuizzes(QuizHelper.importQuizzes(data, true)))
            setDialogText(
              'Only questions were found in this file. Would you like to import questions instead?',
            )
            setDialogTitle('Import Questions?')
            setShowConfirm(true)
          }
          break
        case 'Questions':
          if (topics?.length) {
            setConfirm(() => () => confirmTopics(TopicHelper.importTopics(data, true)))
            setCancel(() => () => confirmQuestions(questions))
            setDialogText(
              'Topics were found in this file. Would you like to import topics instead?',
            )
            setDialogTitle('Import Topics?')
            setShowConfirm(true)
          } else if (quizzes?.length) {
            setConfirm(() => () => confirmQuizzes(QuizHelper.importQuizzes(data, true)))
            setCancel(
              () => () =>
                confirmQuestions(questions.filter((question) => question.question_type !== 'Card')),
            )
            setDialogText(
              'Quizzes were found in this file. Would you like to import quizzes instead?',
            )
            setDialogTitle('Import Quizzes?')
            setShowConfirm(true)
          } else if (questions?.filter((question) => question.question_type !== 'Card').length) {
            confirmQuestions(questions.filter((question) => question.question_type !== 'Card'))
          } else {
            setError('File must contain at least one question.')
          }
          break
        default:
          break
      }
    } catch (e) {
      setError(
        'There was an issue processing your content, please double check your template and try again.',
      )
    }
  }

  return showConfirm ? (
    <ConfirmationDialog
      open={showConfirm}
      title={dialogTitle}
      dialogText={dialogText}
      onConfirm={confirm}
      cancelBtnLabel="No"
      confirmBtnLabel="Yes"
      onCancel={cancel}
      onClose={onClose}
      disableBackdropClick
      hideCloseIcon
      confirmationMessage={''}
    />
  ) : (
    <Dialog maxWidth="sm" title={title} onClose={onClose} open={open}>
      <Stack useFlexGap gap={2} flexDirection="column">
        <FileInput
          file={file}
          mainText="or drag a file right here"
          helperText="Supports Excel, CSV, or Google Sheets"
          accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
          onChange={onDrop}
        />
        {error && file && (
          <Text style={{ marginBottom: 12 }} color="error" text={error} variant="body2Emphasized" />
        )}
        <Text color="text.disabled" text="DOWNLOAD TEMPLATE" variant="body2Emphasized" />
        <Stack flexDirection="row" useFlexGap gap={2}>
          <div
            data-qat="download_excel_template"
            onClick={downloadExcelTemplate}
            style={DL_TEMPLATE}
          >
            <ExcelFile />
            <Text variant="body1Medium" text="Excel Template" />
          </div>
          <div data-qat="download_csv_template" onClick={downloadCsvTemplate} style={DL_TEMPLATE}>
            <ExcelFile />
            <Text variant="body1Medium" text="CSV Template" />
          </div>
          <a target="_blank" href={TEMPLATE_URL} style={DL_TEMPLATE}>
            <ExcelFile />
            <Text variant="body1Medium" text="Google Sheet Template" />
          </a>
        </Stack>
      </Stack>
    </Dialog>
  )
})

const DL_TEMPLATE: React.CSSProperties = {
  textDecoration: 'none',
  color: color.black,
  display: 'flex',
  backgroundColor: color.palette.athensGrey,
  borderRadius: 10,
  height: 56,
  alignItems: 'center',
  cursor: 'pointer',
  padding: 20,
  flexDirection: 'row',
  gap: 12,
}
